import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import { Button, Tooltip, useToast } from "@chakra-ui/react";
import { Breadcrumb, ScoreMeter } from "../../Components/Atoms";
import { RideMap } from "../../Components/Molecules";

import PATHS from "../../Routes/paths";
import { getStatLabel } from "../../Library/helpers";
import { computeTLStats, sphericalCosinus } from "../../Library/geodist";

import "./styles.css";
import { getDriversInfo, getDriverOrVehicleStats, getAlerts} from "../../APIs";
import { DownloadIcon, InfoIcon } from "../../Assets/images/svgs";
import Loader from "../Loader";
import { getDateEnd, getDateStart, getLocalisedDate, getLocalisedTime } from "../../Library/datetime";
import { StoreContext } from "../../Library/contexts";
import { GEOFENCE_TRIGGER_TYPE, DEVICE_HWTYPS } from "../../Library/constants";
import { handleReportDownload } from "../../Library/jsonUtils";

const DAILY_STAT_TYPES = [
  {  title: "Ride Date",       format: "date",       key: "ts"   },
  {  title: "Ride Start Time", format: "time",       key: "sdt"  },
  {  title: "Ride End Time",   format: "time",       key: "edt"  },
  {  title: "Total Distance",  format: "distance",   key: "dst"  },
  {  title: "Average Speed",   format: "speed"   ,   key: "avs"  },
  {  title: "Top Speed",       format: "speed"   ,   key: "tps"  },
  {  title: "Total Duration",  format: "duration",   key: "dur"  },
];

const RIDING_EVENTS = [
  { key: "HARSH_ACCELERATION", label: "Harsh Acceleration" },
  { key: "SHARP_TURN", label: "Sharp Turn" },
  { key: "SUDDEN_BRAKE", label: "Sudden Breaking" },
  { key: "OVERSPEED", label: "Overspeed Limit" },
];

const RideDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userVehicles, allGeofences, setAllGeofences } = useContext(StoreContext);
  const [ ride, setRide ] = useState((location.state && location.state.ride) || {});
  const [ vid, setVid] = useState(null);
  const isWireless = useRef(false);
  const [ vehicleDetails, setVehicleDetails] = useState({});
  const [ showExistingGeofences, setShowExistingGeofences ] = useState(false);
  const [ geofencesToShow, setGeofencesToShow ] = useState(false);

  const searchParams = new URLSearchParams(window.location.search);
  const reg = searchParams.get("reg");
  const ts = searchParams.get("ts");
  const isUser = useRef();
  const isRideDataFetched = useRef(false);
  const Toast = useToast();

  const paths = useMemo(() => {
    const state = { reg, vid };

    const navDetails = (e) => {
      e.preventDefault();
      navigate([PATHS.LEASED_VEHICLES, PATHS.VEHICLE_STATS].join("/") + "?idx=" + vid, { state });
    };

    const navRides = (e) => {
      e.preventDefault();
      navigate([PATHS.LEASED_VEHICLES, PATHS.PAST_RIDES].join("/") + "/" + vid, { state });
    };

    return [
      { idx: 1, name: "Lease", link: PATHS.LEASE },
      { idx: 2, name: "Vehicles", link: PATHS.LEASED_VEHICLES },
      { idx: 3, name: (reg || vid), cb: navDetails },
      { idx: 3, name: "Past Rides", cb: navRides },
      { idx: 3, name: "Ride Details", isCurrentPage: true },
    ];
  }, [ navigate, reg, vid ]);

  const userPaths = useMemo(() => {
    const state = { reg, vid };
    const navRides = (e) => {
      e.preventDefault();
      navigate(`${PATHS.LIVE_TRACKING}/${PATHS.PAST_RIDES}/${vid}${location.search || ""}`, { state });
    };

    return [
      { idx: 1, name: "Live Tracking", link: PATHS.LIVE_TRACKING },
      { idx: 2, name: reg, link: `${PATHS.LIVE_TRACKING}/${PATHS.VEHICLE_STATS}?idx=${vid}&lt=true` },
      { idx: 3, name: "Past Rides", cb: navRides },
      { idx: 3, name: "Ride Details", isCurrentPage: true },
    ];
  }, [location.search, navigate, reg, vid]);

  const [ isLoading, setIsLoading ] = useState(true);
  const [ rideData, setRideData ] = useState({});
  const originalData = useRef([]);
  const pingData = useRef([]);
  const pathData = useRef([]);
  const pitstopData = useRef([]);

  const [ driverData, setDriverData ] = useState(null);
  const [ mapCenter, setMapCenter ] = useState({});

  useEffect(() => {
    let user = window.localStorage.getItem("user");
    try {
      user = JSON.parse(user);
    } catch (e) {
      console.error(e);
    }

    if (user) isUser.current = user;

    const fetchRideData = () => {
      if (isRideDataFetched.current || !Number(vid)) return;

      let data = { vid: Number(vid), tsFr: Math.floor(ride.sdt), tsTo: Math.floor(ride.edt), orphan: false };
      if (isUser.current) {
        data.isWeb = true;
        data.fromTs = data.tsFr;
        data.toTs = data.tsTo;
      }
      global.WS.sendRequest({
        action: !isUser.current ? ("getWirelessPings" || "getTLLocEvents") : "getLocEvents",
        data: data,
      });
    };

    if (global.WS && global.WS.wsConnected) {
      fetchRideData();
    } else global.EE.addEventListener(global.EE.EVENT_TYPES.WS_CONNECTED, fetchRideData);

    return () => {
      global.EE.removeEventListener(global.EE.EVENT_TYPES.WS_CONNECTED, fetchRideData);
    }
  }, [ride, vid]);

  const onRideData = (ping) => {
    if (ping.ts !== 0) {
      pingData.current.push(ping);
    } else {
      isRideDataFetched.current = true;
      pingData.current.sort((a, b) => (a.ts - b.ts));
      if (!originalData.current.length) {
        originalData.current = [ ...pingData.current ];
      }

      let dailyRideFunc = computeTLStats;
      let rideData = dailyRideFunc(pingData.current);

      // If !Wireless Device
      // pingData.filter((ping) => ping.ignition);

      let pitstops = [];    // Points where Rider Stopped for at least 20 minutes
      let prevPing = null;
      pingData.current.forEach((ping, i) => {
        if (!ping.lat || !ping.lng) return;

        // Filtering Out Consecutive Ignition OFF Pings to Reduce Fluctuation
        let lastPing = pingData.current[i - 1];
        if (!isUser.current) {
          if (lastPing && !lastPing.ignition && !ping.ignition) return;
          if (!lastPing && !ping.ignition) return;
        }

        if (prevPing && (ping.ts - prevPing.ts >= (20 * 60))) {
          // Check in Pitstop
          let matched = pitstops.find((pitstop) => {
            let distance = sphericalCosinus(pitstop.location.lat, pitstop.location.lng, ping.lat, ping.lng);
            return (distance < 100);
          });
          if (!matched) {
            pitstops.push({
              location: { lat: ping.lat, lng: ping.lng },
              entries: [{ from: prevPing, to: ping }],
            });
          } else {
            matched.entries.push({ from: prevPing, to: ping });
          }
        }
        if(isWireless.current || (ping.ignition || (ping.motion && ping.spd >= 3))) pathData.current.push(ping);
        prevPing = ping;
      });

      pitstopData.current = pitstops;

      if (pathData.current.length > 2) {
        setMapCenter({
          lat: pathData.current[Math.round(pathData.current.length / 2)].lat,
          lng: pathData.current[Math.round(pathData.current.length / 2)].lng,
        });
      }

      global.gPingData = pingData;
      global.gRideData = rideData;
      global.gPitstops = pitstops;
      global.gPathData = pathData;
      if (location.state && location.state.ride) {
        setRideData({
          meta: rideData,
          stats: DAILY_STAT_TYPES.map((stat, i) => ({
            idx: i,
            title: stat.title,
            label: getStatLabel(rideData[stat.key] || ride[stat.key], stat.format, 1),
          })),
        });
      }
      setIsLoading(false);
    }
  };

  useEffect(() => {
    global.EE.addEventListener(global.EE.EVENT_TYPES.ON_VEHICLE_DATA, onRideData);

    return () => {
      global.EE.removeEventListener(global.EE.EVENT_TYPES.ON_VEHICLE_DATA, onRideData);
    };
  }, []);

  useEffect(() => {
    // Fetch Driver Details on Page Load
    if (ride && ride.drId) {
      getDriversInfo(null, ride.drId, false, null, (_, driver) => {
        if (driver && driver[0] && driver[0].dNm) setDriverData(driver[0]);
      });
    }

    let range = { from: getDateStart(Number(ts)), to: getDateEnd(Number(ts)) };

    if (vid && (!ride || !ride.ts)) {
      getDriverOrVehicleStats(vid, null, range,  (err, res) => {
        if (err) {
          console.error("Error Fetching Ride Data", err);
        } else {
          if (res.dayWiseStats && res.dayWiseStats.length) {
            let ride = res.dayWiseStats.find(ride => ride.ts === Number(ts));
            if (ride) {
              setRide(ride);
              setRideData({
                meta: ride,
                stats: DAILY_STAT_TYPES.map((stat, i) => ({
                  idx: i,
                  title: stat.title,
                  label: getStatLabel(ride[stat.key] || 0, stat.format, 1),
                })),
              })
            }
          }
        }
      });
    }
  }, [ride, ts, vid]);

  useEffect(() => {
    if (userVehicles && userVehicles.length) {
      let vehicle = userVehicles.find(vehicle => vehicle.reg === reg);
      if (vehicle) {
        setVehicleDetails(vehicle);
        setVid(vehicle.idx);
        if(vehicle.hwtyp == DEVICE_HWTYPS["Wireless"]) isWireless.current = true;
      }
      else navigate(PATHS.LEASED_VEHICLES);
    }
  }, [userVehicles, reg, navigate]);

  const getDriver = () => {
    if (!driverData) return <span>{"-"}</span>
    return (
      <Link className="text-blue" to={`${PATHS.LEASE}/${PATHS.DRIVERS}/${PATHS.DRIVER_STATS}?idx=${ride.drId}`}>
        {driverData.dNm}
      </Link>
    );
  };

  if (!ts || !reg) return <Navigate to={PATHS.LEASED_VEHICLES} />;

  const getFilteredRideData = (from, to) => {
    if (from && to) {
      from = from / 1000;
      to = to / 1000;

      pingData.current = [];
      pathData.current = [];
      pitstopData.current = [];
      setIsLoading(true);

      pingData.current = originalData.current.filter(ping => {
        return (ping.ts >= from && ping.ts <= to);
      })

      onRideData({ ts: 0 });
    }
  }


  const getDistanceBreakdown = () => {
    if (rideData.meta?.dayDist) {
      let start = new Date(rideData.meta.sdt * 1000);
      let end = new Date(rideData.meta.edt * 1000);

      let startDate = start.toLocaleDateString("en-IN", { day: "2-digit", month: "long" });
      let endDate = end.toLocaleDateString("en-IN", { day: "2-digit", month: "long" });

      let startTime = start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
      let endTime = end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });

      return `This includes the distance between ${startTime} (${startDate}) to ${endTime} (${endDate}). The distance travelled on ${startDate} (upto 11:59pm) is ${(rideData.meta.dayDist / 1000).toFixed(2)}kms.`;
    }
  }

  const resetRideData = () => {
    pingData.current = originalData.current;
    pathData.current = [];
    pitstopData.current = [];

    onRideData({ ts: 0 });
  }

  const isSameDay = rideData.meta && (new Date(rideData.meta.sdt * 1000).getDate() === new Date(rideData.meta?.edt * 1000).getDate());

  const handleGeofences = (isChecked) => {
    if (!allGeofences || !allGeofences.length) {
      getAlerts(GEOFENCE_TRIGGER_TYPE, (err, data) => {
        if(err) console.error("Error fetching geofence alerts", err);
        else {

          setAllGeofences(data);
          let geofenceForSelVehicle = data.filter(alert => alert.vehIdArr && alert.vehIdArr.includes(vid));
          setGeofencesToShow(geofenceForSelVehicle || []);
          if (!data.length) Toast({ isClosable: true, description: "You haven't added any geofences yet", position: "bottom", variant: "subtle", duration: 3000 })
        }
      });
    } else {
      setAllGeofences(allGeofences);
      let geofenceForSelVehicle = allGeofences.filter(alert => alert.vehIdArr && alert.vehIdArr.includes(vid));
      setGeofencesToShow(geofenceForSelVehicle || []);
    }

    setShowExistingGeofences(isChecked);
  }

  const handleRideReport = (ridedata) => {
    let dataToDownload = [];
    let totalDst = 0;
    ridedata.forEach((ping, idx) => {
      let prevPing = ridedata[idx - 1];
      let dst = 0;
      if (prevPing && prevPing.lat && prevPing.lng) dst = sphericalCosinus(ping.lat, ping.lng, prevPing.lat, prevPing.lng);
      if (dst) {
        dst = (dst / 1000);
        totalDst += dst;
      }
      dataToDownload.push({ idx: idx + 1, date: getLocalisedDate(ping.ts * 1000, true), time: getLocalisedTime(ping.ts * 1000, true, null, true), speed: ping.spd, distance: dst, totalDistance: totalDst });
    });

    if (dataToDownload && dataToDownload.length) handleReportDownload(dataToDownload);
  }

  return (
    <>
      {!isLoading ? <div className="panel-main past-rides ride-details">
        <header>
          <Breadcrumb paths={isUser.current ? userPaths : paths} />
        </header>

        {isLoading ? (
          <div />
        ) : ( (rideData && rideData.stats) ?
            <>
              <div className="ride-wrapper">
                <article className="card widget-group">
                <div className="ride-stats-head">
                  <h3>Ride Stats</h3>
                  {isUser.current ? <hr /> : null}
                </div>
                <Tooltip label={"Download ride report"} hasArrow placement="left">
                  <Button className="download-ride-report-btn" onClick={() => handleRideReport(pathData.current)}>
                    <DownloadIcon />
                  </Button>
                </Tooltip>

                <div className="widget-group keyvals">
                    {[...rideData.stats].map((stat) => (
                      <main className="widget-main" key={stat.idx}>
                        <header>{stat.title}</header>
                        <article>
                          {stat.label}
                          {stat.title === "Total Distance" ? (
                            (!isSameDay && <Tooltip className="distance-tooltip" closeOnScroll={false} hasArrow label={getDistanceBreakdown()} bg="#fff" color="black" placement="top">
                              <InfoIcon className="distance-info" />
                            </Tooltip>)
                          ) : ""}
                        </article>
                      </main>
                    ))}
                    {!isUser.current ? <main className="widget-main">
                      <header>Driver Name</header>
                      <article>{getDriver()}</article>
                    </main> : null}
                  </div>
                </article>
                {ride.rideScoreCounts ?
                  <article className="card widget-group">
                    <div className="ride-stats-head">
                      <h3>Riding Score</h3>
                      <hr />
                    </div>
                  <div className="ride-score-container">
                    <div className="meter-container">
                      <ScoreMeter score={ride?.rideScore} showAboutScore={false}/>
                    </div>
                    <div className="riding-events">
                      {
                        RIDING_EVENTS.map((event, idx)=>(
                          <div className="riding-score-val" key={idx}>
                            <span className="val">{ride.rideScoreCounts ? ride.rideScoreCounts[event.key] : 0}</span>
                            <span className="abt">{event.label}</span>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                  </article> : null}
              </div>
              {pathData.current && pathData.current.length ?
                <div className="ride-map-container">
                  <RideMap geofencesToPlot={geofencesToShow} showExistingGfsToggle={true} showExistingGeofences={showExistingGeofences} setShowExistingGeofences={handleGeofences} vehicleDetails={vehicleDetails} handleRideFilterReset={resetRideData} handleRideFilter={getFilteredRideData} pathTrail={pathData.current} pitstops={pitstopData.current} mapCenter={mapCenter} />
                </div>
              : null}
            </>
            : <span className="no-data-msg">No data available !!</span>
        )}
      </div> : <Loader hide={true} />}
    </>
  );
};


export default RideDetails;